/**
 * Runtime Configuration Utility
 *
 * This utility provides a unified way to access configuration values that works in both:
 * 1. Production/K8s: Reads from window.config (generated at container startup from ConfigMap)
 * 2. Local Development: Falls back to process.env (from .env file)
 *
 * This approach allows the same Docker image to be used across multiple environments
 * with different configurations without rebuilding.
 */

// Extend the Window interface to include our config object
declare global {
  interface Window {
    config?: {
      REACT_APP_API_BASE_URL?: string;
      REACT_APP_KEYCLOAK_URL?: string;
      REACT_APP_KEYCLOAK_REALM?: string;
      REACT_APP_KEYCLOAK_CLIENT_ID?: string;
      REACT_APP_KEYCLOAK_REDIRECT_URI?: string;
    };
  }
}

/**
 * Get configuration value with fallback
 * Priority: window.config (runtime) > process.env (build-time)
 */
function getConfigValue(key: string, fallback: string = ''): string {
  // Try runtime config first (from config.js)
  if (window.config && window.config[key as keyof typeof window.config]) {
    return window.config[key as keyof typeof window.config] || fallback;
  }

  // Fallback to process.env for local development
  return process.env[key] || fallback;
}

/**
 * Runtime Configuration Object
 * Exported for use throughout the application
 */
export const runtimeConfig = {
  // API Configuration
  apiBaseUrl: getConfigValue('REACT_APP_API_BASE_URL'),

  // Keycloak Configuration
  keycloakUrl: getConfigValue('REACT_APP_KEYCLOAK_URL'),
  keycloakRealm: getConfigValue('REACT_APP_KEYCLOAK_REALM'),
  keycloakClientId: getConfigValue('REACT_APP_KEYCLOAK_CLIENT_ID'),
  keycloakRedirectUri: getConfigValue('REACT_APP_KEYCLOAK_REDIRECT_URI', window.location.origin),
};

/**
 * Validate that all required configuration values are present
 */
export function validateConfig(): { valid: boolean; missing: string[] } {
  const required = {
    'REACT_APP_API_BASE_URL': runtimeConfig.apiBaseUrl,
    'REACT_APP_KEYCLOAK_URL': runtimeConfig.keycloakUrl,
    'REACT_APP_KEYCLOAK_REALM': runtimeConfig.keycloakRealm,
    'REACT_APP_KEYCLOAK_CLIENT_ID': runtimeConfig.keycloakClientId,
  };

  const missing = Object.entries(required)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  return {
    valid: missing.length === 0,
    missing,
  };
}

/**
 * Log configuration status (useful for debugging)
 */
export function logConfigStatus(): void {
  const validation = validateConfig();
  const configSource = window.config ? 'window.config (runtime)' : 'process.env (local)';

  console.log('🔧 Configuration loaded from:', configSource);
  console.log('📍 API Base URL:', runtimeConfig.apiBaseUrl);
  console.log('🔐 Keycloak URL:', runtimeConfig.keycloakUrl);
  console.log('🌍 Keycloak Realm:', runtimeConfig.keycloakRealm);
  console.log('🔑 Keycloak Client ID:', runtimeConfig.keycloakClientId);

  if (!validation.valid) {
    console.error('❌ Missing configuration values:', validation.missing);
  } else {
    console.log('✅ All configuration values present');
  }
}

// Log config status in development mode
if (process.env.NODE_ENV === 'development') {
  logConfigStatus();
}

export default runtimeConfig;
